import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Row, Col, Alert, Toast, ToastContainer } from 'react-bootstrap';
import { emailTemplateService } from '../../services/api/emailTemplateService';
import { stageService } from '../../services/api/stageService';
import LoadingSpinner from '../common/LoadingSpinner';
import { EmailTemplate, Stage } from '../../types/types';
import EmailPreviewModal from './EmailPreviewModal';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const EmailTemplateManagement: React.FC = () => {
    const [templates, setTemplates] = useState<EmailTemplate[]>([]);
    const [stages, setStages] = useState<Stage[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // Modal states
    const [showModal, setShowModal] = useState<boolean>(false);
    const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [currentTemplate, setCurrentTemplate] = useState<EmailTemplate | null>(null);

    // Form states
    const [name, setName] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [subject, setSubject] = useState<string>('');
    const [htmlBody, setHtmlBody] = useState<string>('');
    const [stageId, setStageId] = useState<string>('');
    const [isActive, setIsActive] = useState<boolean>(true);
    const [hasCalendarInvite, setCalendarInvite] = useState<boolean>(false);

    // Toast notification
    const [toast, setToast] = useState<{ show: boolean; message: string; variant: string }>({
        show: false,
        message: '',
        variant: 'success'
    });

    // Delete confirmation
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [templateToDelete, setTemplateToDelete] = useState<EmailTemplate | null>(null);

    // Rich text editor modules and formats configuration
    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'align': [] }],
            ['link', 'image'],
            ['clean']
        ]
    };

    const quillFormats = [
        'header',
        'bold', 'italic', 'underline', 'strike',
        'color', 'background',
        'list', 'bullet',
        'align',
        'link', 'image'
    ];

    // Load templates and stages on component mount
    useEffect(() => {
        fetchTemplatesAndStages();
    }, []);

    const fetchTemplatesAndStages = async () => {
        setLoading(true);
        try {
            const [templatesResponse, stagesResponse] = await Promise.all([
                emailTemplateService.getAllEmailTemplates(),
                stageService.getActiveStages()
            ]);

            setTemplates(templatesResponse.items);
            setStages(stagesResponse);
            setError(null);
        } catch (err) {
            setError('Failed to load data. Please try again later.');
            console.error('Error fetching data:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        resetForm();
    };

    const handleOpenAddModal = () => {
        setIsEditing(false);
        setCurrentTemplate(null);
        resetForm();
        setShowModal(true);
    };

    const handleOpenEditModal = (template: EmailTemplate) => {
        setIsEditing(true);
        setCurrentTemplate(template);
        // Populate form with template details
        setName(template.name);
        setDescription(template.description || '');
        setSubject(template.subject);
        setHtmlBody(template.htmlBody);
        setStageId(template.stageId || '');
        setIsActive(template.isActive);
        setCalendarInvite(template.hasCalendarInvite);
        setShowModal(true);
    };

    const resetForm = () => {
        setName('');
        setDescription('');
        setSubject('');
        setHtmlBody('');
        setStageId('');
        setIsActive(true);
    };

    const showToast = (message: string, variant: string = 'success') => {
        setToast({
            show: true,
            message,
            variant
        });
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        setLoading(true);
        try {
            if (isEditing && currentTemplate) {
                const updateData = {
                    id: currentTemplate.id,
                    name,
                    description,
                    subject,
                    htmlBody,
                    stageId: stageId || null,
                    isActive,
                    hasCalendarInvite
                };
                await emailTemplateService.updateEmailTemplate(currentTemplate.id, updateData);
                showToast('Template updated successfully!');
            } else {
                const createData = {
                    name,
                    description,
                    subject,
                    htmlBody,
                    stageId: stageId || null,
                    isActive,
                    hasCalendarInvite
                };
                await emailTemplateService.createEmailTemplate(createData);
                showToast('Template created successfully!');
            }

            await fetchTemplatesAndStages();
            handleCloseModal();
        } catch (err) {
            setError(`Failed to ${isEditing ? 'update' : 'create'} template. Please try again.`);
            console.error(`Error ${isEditing ? 'updating' : 'creating'} template:`, err);
        } finally {
            setLoading(false);
        }
    };

    const confirmDelete = (template: EmailTemplate) => {
        setTemplateToDelete(template);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        if (!templateToDelete) return;

        setLoading(true);
        try {
            await emailTemplateService.deleteEmailTemplate(templateToDelete.id);
            await fetchTemplatesAndStages();
            setShowDeleteModal(false);
            setTemplateToDelete(null);
            showToast('Template deleted successfully!');
        } catch (err) {
            setError('Failed to delete template. Please try again.');
            console.error('Error deleting template:', err);
        } finally {
            setLoading(false);
        }
    };

    const handlePreview = (template: EmailTemplate) => {
        setCurrentTemplate(template);
        setShowPreviewModal(true);
    };

    const getStageName = (stageId?: string) => {
        if (!stageId) return 'None';
        const stage = stages.find(s => s.id === stageId);
        return stage ? stage.name : 'Unknown';
    };

    // Helper to truncate long text for display in table
    const truncateText = (text: string, maxLength: number = 50) => {
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    // Handle rich text editor content change
    const handleEditorChange = (content: string) => {
        setHtmlBody(content);
    };

    return (
        <>
            <Card className="shadow-sm">
                <Card.Header className="bg-white">
                    <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Email Template Management</h5>
                        <Button variant="primary" onClick={handleOpenAddModal}>
                            <i className="bi bi-plus-circle me-1"></i> Add New Template
                        </Button>
                    </div>
                </Card.Header>
                <Card.Body>
                    {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}

                    {loading && !showModal && <LoadingSpinner centered />}

                    {!loading && templates.length === 0 && (
                        <div className="text-center py-5">
                            <p className="text-muted">No email templates found. Click "Add New Template" to create one.</p>
                        </div>
                    )}

                    {!loading && templates.length > 0 && (
                        <div className="table-responsive">
                            <Table striped bordered hover>
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Subject</th>
                                        <th>Associated Stage</th>
                                        <th>Status</th>
                                        <th>Last Modified</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {templates.map((template) => (
                                        <tr key={template.id}>
                                            <td>{template.name}</td>
                                            <td title={template.subject}>{truncateText(template.subject)}</td>
                                            <td>{getStageName(template.stageId)}</td>
                                            <td>
                                                <span className={`badge ${template.isActive ? 'bg-success' : 'bg-secondary'}`}>
                                                    {template.isActive ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td>{new Date(template.updatedAt || template.createdAt).toLocaleDateString()}</td>
                                            <td>
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    className="me-1"
                                                    title="Preview Template"
                                                    onClick={() => handlePreview(template)}
                                                >
                                                    <i className="bi bi-eye"></i>
                                                </Button>
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="me-1"
                                                    title="Edit Template"
                                                    onClick={() => handleOpenEditModal(template)}
                                                >
                                                    <i className="bi bi-pencil"></i>
                                                </Button>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    title="Delete Template"
                                                    onClick={() => confirmDelete(template)}
                                                >
                                                    <i className="bi bi-trash"></i>
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Card.Body>

                {/* Add/Edit Template Modal */}
                <Modal show={showModal} onHide={handleCloseModal} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>{isEditing ? 'Edit Email Template' : 'Add New Email Template'}</Modal.Title>
                    </Modal.Header>
                    <Form onSubmit={handleSubmit}>
                        <Modal.Body>
                            <Row>
                                <Col md={8}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Template Name*</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter template name"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Associated Stage</Form.Label>
                                        <Form.Select
                                            value={stageId}
                                            onChange={(e) => setStageId(e.target.value)}
                                        >
                                            <option value="">None (General Template)</option>
                                            {stages.map(stage => (
                                                <option key={stage.id} value={stage.id}>
                                                    {stage.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={12}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Description*</Form.Label>
                                        <Form.Control
                                            type="textarea"
                                            placeholder="Enter template description"
                                            value={description}
                                            onChange={(e) => setDescription(e.target.value)}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Form.Group className="mb-3">
                                <Form.Label>Subject Line*</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter email subject"
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    required
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Email Body*</Form.Label>
                                {/* Replace textarea with ReactQuill rich text editor */}
                                <div className="rich-text-editor-container border rounded">
                                    <ReactQuill
                                        theme="snow"
                                        value={htmlBody}
                                        onChange={handleEditorChange}
                                        modules={quillModules}
                                        formats={quillFormats}
                                        style={{ height: '300px' }}
                                    />
                                </div>
                                <Form.Text className="text-muted mt-2">
                                    Available placeholders: {'{{CandidateFirstName}}'}, {'{{CandidateLastName}}'}, {'{{CandidateFullName}}'},
                                    {'{{CandidateEmail}}'}, {'{{StageName}}'}, {'{{JobTitle}}'}, {'{{CustomMessage}}'}, {'{{CompanyName}}'}, {'{{Date}}'}
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Check
                                    type="checkbox"
                                    label="Active"
                                    checked={isActive}
                                    onChange={(e) => setIsActive(e.target.checked)}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Check
                                    type="checkbox"
                                    label="Has Calendar Invite"
                                    checked={hasCalendarInvite}
                                    onChange={(e) => setCalendarInvite(e.target.checked)}
                                />
                            </Form.Group>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleCloseModal}>
                                Cancel
                            </Button>
                            <Button variant="primary" type="submit" disabled={loading}>
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Saving...
                                    </>
                                ) : (
                                    'Save Template'
                                )}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </Modal>

                {/* Enhanced Email Preview Modal */}
                {currentTemplate && (
                    <EmailPreviewModal
                        show={showPreviewModal}
                        onHide={() => setShowPreviewModal(false)}
                        templateId={currentTemplate.id}
                        templateName={currentTemplate.name}
                    />
                )}

                {/* Delete Confirmation Modal */}
                <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <i className="bi bi-exclamation-triangle me-2 text-danger"></i>
                            Confirm Delete
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        Are you sure you want to delete the email template "{templateToDelete?.name}"? This action cannot be undone.
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="danger" onClick={handleDelete} disabled={loading}>
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Deleting...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-trash me-1"></i> Delete Template
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </Card>

            {/* Toast Container - Fixed position at the top right */}
            <ToastContainer position="top-end" className="p-3" style={{ zIndex: 1050 }}>
                <Toast
                    onClose={() => setToast(prev => ({ ...prev, show: false }))}
                    show={toast.show}
                    delay={3000}
                    autohide
                    bg={toast.variant}
                >
                    <Toast.Header>
                        <strong className="me-auto">
                            {toast.variant === 'success' ? 'Success' : 'Notification'}
                        </strong>
                    </Toast.Header>
                    <Toast.Body className={toast.variant === 'success' ? 'text-white' : ''}>
                        {toast.message}
                    </Toast.Body>
                </Toast>
            </ToastContainer>
        </>
    );
};

export default EmailTemplateManagement;